/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { simpleFetch } from "../../restWrapper";
import { photoUrl } from "../../assets/media";
import PaymentMethodModal from "../OrgSettings/component/PaymentMethodModal";
import "./MediaGalleryModal.css";

// The gallery is unbounded; images load a page at a time via "Load more".
// Matches DEFAULT_PAGE_SIZE in routes/organizations/media_assets.js.
const PAGE_SIZE = 10;
const DESCRIPTION_MAX_LENGTH = 200;

// The list endpoint returns { items, total }. Tolerate a bare array too, so a
// new frontend paired with a not-yet-deployed backend still renders the first
// page instead of showing a load error.
const normalizeList = (body) => {
  if (Array.isArray(body)) return { items: body, total: body.length };
  if (body && Array.isArray(body.items)) {
    return {
      items: body.items,
      total: typeof body.total === "number" ? body.total : body.items.length,
    };
  }
  return null;
};

// Offset paging over a collection the backfill may have just grown can hand
// back an item we already hold; keep the first copy of each id.
const mergeById = (existing, incoming) => {
  const seen = new Set(existing.map((i) => i.id));
  return existing.concat(incoming.filter((i) => !seen.has(i.id)));
};

class MediaGalleryModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      total: 0,
      loading: true,
      loadingMore: false,
      uploading: false,
      error: "",
      blockedProjects: null,
      confirmDeleteId: null,
      deletingId: null,
      editingDescId: null,
      editingDescValue: "",
      savingDescId: null,
    };
    this.handleFileChange = this.handleFileChange.bind(this);
    this.loadMore = this.loadMore.bind(this);
  }

  componentDidMount() {
    this.loadFirstPage();
  }

  t = (key, fallback) => {
    const block =
      this.props.languageObj && typeof this.props.languageObj === "object"
        ? this.props.languageObj
        : {};
    return block[key] || fallback;
  };

  fetchPage(offset) {
    return simpleFetch("/organizations/media-assets", "GET", {
      offset,
      limit: PAGE_SIZE,
    }).then((res) => (res.status === 200 ? normalizeList(res.body) : null));
  }

  loadFirstPage() {
    this.setState({ loading: true, error: "", blockedProjects: null });
    this.fetchPage(0)
      .then((parsed) => {
        if (!parsed) {
          this.setState({
            loading: false,
            error: this.t("galleryLoadFailed", "Couldn't load images. Please try again."),
          });
          return;
        }
        this.setState({
          items: parsed.items,
          total: parsed.total,
          loading: false,
        });
      })
      .catch(() => {
        this.setState({
          loading: false,
          error: this.t("galleryLoadFailed", "Couldn't load images. Please try again."),
        });
      });
  }

  loadMore() {
    if (this.state.loadingMore) return;
    this.setState({ loadingMore: true, error: "", blockedProjects: null });
    this.fetchPage(this.state.items.length)
      .then((parsed) => {
        if (!parsed) {
          this.setState({
            loadingMore: false,
            error: this.t("galleryLoadFailed", "Couldn't load images. Please try again."),
          });
          return;
        }
        this.setState((prev) => ({
          items: mergeById(prev.items, parsed.items),
          total: parsed.total,
          loadingMore: false,
        }));
      })
      .catch(() => {
        this.setState({
          loadingMore: false,
          error: this.t("galleryLoadFailed", "Couldn't load images. Please try again."),
        });
      });
  }

  handleFileChange(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      this.setState({
        error: this.t("galleryFileType", "Only PNG or JPEG images are allowed."),
        blockedProjects: null,
      });
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    this.setState({ uploading: true, error: "", blockedProjects: null });
    simpleFetch("/organizations/media-assets", "POST", formData, true)
      .then((res) => {
        if (res.status === 200 && res.body) {
          this.setState((prev) => ({
            items: [res.body, ...prev.items],
            total: prev.total + 1,
            uploading: false,
          }));
        } else {
          this.setState({
            uploading: false,
            error: this.t("galleryUploadFailed", "Upload failed. Please try again."),
          });
        }
      })
      .catch(() => {
        this.setState({
          uploading: false,
          error: this.t("galleryUploadFailed", "Upload failed. Please try again."),
        });
      });
  }

  handleDeleteClick(item) {
    // Already in flight — ignore repeat clicks rather than firing a second
    // DELETE for the same image.
    if (this.state.deletingId === item.id) return;
    if (this.state.confirmDeleteId !== item.id) {
      this.setState({
        confirmDeleteId: item.id,
        error: "",
        blockedProjects: null,
      });
      return;
    }
    // The round trip isn't instant — it checks every project for usage before
    // deleting the S3 object — so hold a per-item pending state.
    this.setState({ confirmDeleteId: null, deletingId: item.id });
    simpleFetch("/organizations/media-assets/" + item.id, "DELETE")
      .then((res) => {
        if (res.status === 204) {
          this.setState((prev) => ({
            items: prev.items.filter((i) => i.id !== item.id),
            total: Math.max(0, prev.total - 1),
            deletingId: null,
          }));
          return;
        }
        // 409 — still used as a project header image.
        if (res.status === 409) {
          const projects =
            res.body && Array.isArray(res.body.projects) ? res.body.projects : [];
          this.setState({
            deletingId: null,
            error: this.t(
              "galleryDeleteInUse",
              "This image is still used by a project header. Remove it from the project first."
            ),
            blockedProjects: projects,
          });
          return;
        }
        this.setState({
          deletingId: null,
          error: this.t("galleryDeleteFailed", "Delete failed. Please try again."),
        });
      })
      .catch(() => {
        this.setState({
          deletingId: null,
          error: this.t("galleryDeleteFailed", "Delete failed. Please try again."),
        });
      });
  }

  handleSelect(item) {
    if (this.props.mode !== "picker") return;
    /* A whole URL: what the caller does with this is store it on a project,
       and the direct upload path next to it stores one too (OrgSettings
       builds PHOTO_PATH + the returned key). Both now come from the same
       base, so a project header picked from the gallery and one uploaded
       straight to it are written the same way. */
    const url = photoUrl(item.s3Key);
    /* Everywhere else photoUrl feeds an <img src>, where an unresolved value
       costs one blank image and is fixed by fixing the environment. Here it is
       written to the project's headerBackgroundPicture and kept, so a build
       with no REACT_APP_PHOTO_PATH — or a row with no s3Key — would bake a
       string that points nowhere into records that a later fix cannot reach.
       Refuse the selection instead, and say so. */
    if (!/^(https?:)?\/\//i.test(url)) {
      this.setState({
        error: this.t(
          "gallerySelectUnresolved",
          "This image can't be used right now. Please try again later."
        ),
      });
      return;
    }
    this.props.onSelect(url);
  }

  startDescriptionEdit(item) {
    this.setState({
      editingDescId: item.id,
      editingDescValue: item.description || "",
      error: "",
      blockedProjects: null,
      confirmDeleteId: null,
    });
  }

  cancelDescriptionEdit() {
    this.setState({ editingDescId: null, editingDescValue: "" });
  }

  saveDescription(item) {
    const description = this.state.editingDescValue;
    this.setState({ savingDescId: item.id, error: "" });
    simpleFetch("/organizations/media-assets/" + item.id, "PUT", { description })
      .then((res) => {
        if (res.status === 200 && res.body) {
          this.setState((prev) => ({
            items: prev.items.map((i) =>
              i.id === item.id ? { ...i, description: res.body.description } : i
            ),
            editingDescId: null,
            editingDescValue: "",
            savingDescId: null,
          }));
          return;
        }
        this.setState({
          savingDescId: null,
          error: this.t(
            "galleryDescriptionFailed",
            "Couldn't save the description. Please try again."
          ),
        });
      })
      .catch(() => {
        this.setState({
          savingDescId: null,
          error: this.t(
            "galleryDescriptionFailed",
            "Couldn't save the description. Please try again."
          ),
        });
      });
  }

  handleDescriptionKeyDown(e, item) {
    if (e.key === "Enter") {
      this.saveDescription(item);
      return;
    }
    if (e.key === "Escape") {
      this.cancelDescriptionEdit();
    }
  }

  renderDescription(item) {
    const { editingDescId, editingDescValue, savingDescId } = this.state;

    if (editingDescId === item.id) {
      return (
        <div className="MediaGalleryModal-descEdit">
          <input
            type="text"
            className="MediaGalleryModal-descInput"
            value={editingDescValue}
            maxLength={DESCRIPTION_MAX_LENGTH}
            autoFocus
            placeholder={this.t(
              "galleryDescriptionPlaceholder",
              "Describe this image"
            )}
            aria-label={this.t("galleryDescriptionLabel", "Image description")}
            onChange={(e) => this.setState({ editingDescValue: e.target.value })}
            onKeyDown={(e) => this.handleDescriptionKeyDown(e, item)}
          />
          <div className="MediaGalleryModal-descActions">
            <button
              type="button"
              className="MediaGalleryModal-descSaveBtn"
              disabled={savingDescId === item.id}
              onClick={() => this.saveDescription(item)}
            >
              {savingDescId === item.id
                ? this.t("saving", "Saving...")
                : this.t("save", "Save")}
            </button>
            <button
              type="button"
              className="MediaGalleryModal-descCancelBtn"
              disabled={savingDescId === item.id}
              onClick={() => this.cancelDescriptionEdit()}
            >
              {this.t("cancel", "Cancel")}
            </button>
          </div>
        </div>
      );
    }

    if (item.description) {
      return (
        <div className="MediaGalleryModal-desc" title={item.description}>
          {item.description}
        </div>
      );
    }
    return (
      <div className="MediaGalleryModal-desc MediaGalleryModal-desc--empty">
        {this.t("galleryNoDescription", "No description")}
      </div>
    );
  }

  renderItem(item) {
    const isPicker = this.props.mode === "picker";
    const confirming = this.state.confirmDeleteId === item.id;
    const deleting = this.state.deletingId === item.id;
    const editing = this.state.editingDescId === item.id;
    return (
      <div className="MediaGalleryModal-item" key={item.id}>
        <div
          className={
            "MediaGalleryModal-thumbWrap" +
            (isPicker ? " MediaGalleryModal-thumbWrap--clickable" : "")
          }
          onClick={isPicker ? () => this.handleSelect(item) : undefined}
        >
          <img
            className="MediaGalleryModal-thumb"
            src={photoUrl(item.s3Key)}
            alt={item.description || item.filename || "Gallery image " + item.id}
          />
        </div>
        {/* The original upload filename, so an image can be found by name
            rather than by its id. Absent on images recovered by the backfill,
            which only ever knew their S3 key. */}
        {item.filename ? (
          <div className="MediaGalleryModal-filename" title={item.filename}>
            {item.filename}
          </div>
        ) : null}
        {this.renderDescription(item)}
        <div className="MediaGalleryModal-itemFooter">
          <span className="MediaGalleryModal-itemId">{item.id}</span>
          {/* Describe and Delete are available in picker mode too, so the
              gallery offers the same actions wherever it's opened from. They
              sit outside the clickable thumbnail, so they never trigger a
              selection. `mode` only decides whether clicking a tile picks it. */}
          {!editing ? (
            <span className="MediaGalleryModal-itemActions">
              <button
                type="button"
                className="MediaGalleryModal-descEditBtn"
                disabled={deleting}
                onClick={() => this.startDescriptionEdit(item)}
              >
                {item.description
                  ? this.t("galleryEditDescription", "Edit")
                  : this.t("galleryAddDescription", "Describe")}
              </button>
              {this.props.allowDelete ? (
                <button
                  type="button"
                  className="MediaGalleryModal-deleteBtn"
                  disabled={deleting}
                  onClick={() => this.handleDeleteClick(item)}
                >
                  {deleting
                    ? this.t("galleryDeleting", "Deleting...")
                    : confirming
                      ? this.t("galleryConfirmDelete", "Confirm delete?")
                      : this.t("galleryDelete", "Delete")}
                </button>
              ) : null}
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  renderError() {
    const { error, blockedProjects } = this.state;
    if (!error) return null;
    return (
      <div className="MediaGalleryModal-error">
        {error}
        {blockedProjects && blockedProjects.length > 0 ? (
          <React.Fragment>
            {/* Labelled, so a project title can't read as a fragment of the
                sentence above it. */}
            <div className="MediaGalleryModal-blockedLabel">
              {this.t("galleryDeleteInUseList", "Used by:")}
            </div>
            <ul className="MediaGalleryModal-blockedList">
              {blockedProjects.map((p) => (
                <li key={p.id}>{p.title || "#" + p.id}</li>
              ))}
            </ul>
          </React.Fragment>
        ) : null}
      </div>
    );
  }

  render() {
    const { items, total, loading, loadingMore, uploading } = this.state;
    const hasMore = items.length < total;
    return (
      <PaymentMethodModal
        isOpen
        onClose={this.props.onClose}
        title={this.props.title}
        size="large"
        textObj={{ close: this.t("close", "Close") }}
      >
        <div className="MediaGalleryModal">
          <p className="MediaGalleryModal-intro">
            {this.t(
              "galleryIntro",
              "Every image has a number shown beneath it. Enter that number in the “Image ID” column of the bulk import template to use it as a project header, and add a description so it's easy to find again."
            )}
          </p>

          <label className="MediaGalleryModal-uploadBtn">
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={this.handleFileChange}
              disabled={uploading}
              hidden
            />
            {uploading
              ? this.t("galleryUploading", "Uploading...")
              : this.t("galleryUpload", "Upload image")}
          </label>

          {this.renderError()}

          {loading ? (
            <div className="MediaGalleryModal-message">
              {this.t("galleryLoading", "Loading images...")}
            </div>
          ) : items.length === 0 ? (
            <div className="MediaGalleryModal-message">
              {this.t("galleryEmpty", "No images yet. Upload one to get started.")}
            </div>
          ) : (
            <React.Fragment>
              <div className="MediaGalleryModal-grid">
                {items.map((item) => this.renderItem(item))}
              </div>
              <div className="MediaGalleryModal-footer">
                <span className="MediaGalleryModal-count">
                  {`${this.t("galleryShowing", "Showing")} ${items.length} / ${total}`}
                </span>
                {hasMore ? (
                  <button
                    type="button"
                    className="MediaGalleryModal-loadMoreBtn"
                    onClick={this.loadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore
                      ? this.t("galleryLoading", "Loading images...")
                      : this.t("galleryLoadMore", "Load more")}
                  </button>
                ) : null}
              </div>
            </React.Fragment>
          )}
        </div>
      </PaymentMethodModal>
    );
  }
}

MediaGalleryModal.propTypes = {
  mode: PropTypes.oneOf(["manage", "picker"]).isRequired,
  title: PropTypes.string.isRequired,
  languageObj: PropTypes.object,
  // Set false where the caller holds unsaved project state, which would make
  // the server's in-use check disagree with what the user sees — see CreateGoal.
  allowDelete: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
};

MediaGalleryModal.defaultProps = {
  allowDelete: true,
};

export default MediaGalleryModal;
